import React, { lazy, useEffect, useRef, useState } from "react";
import {
  createDraft,
  updateQuote,
  updateToPending,
} from "../../../backend-services";
import { Button } from "../../common";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ICreateSalesData,
  ICustomer,
  IInventory,
  ItemInput,
} from "../../../@types";
import {
  createDraftData,
  createInvoiceData,
  createInvoiceFreeformData,
  createQuoteFreeformData,
  InvoiceValidationErrors,
} from "../../../modules/invoice/invoice";
import {
  InvoiceErrors,
  validateFreeFormItemDetails,
  validateInvoiceBasicInfo,
  validateInvoiceItemDetails,
  validateInvoiceSummary,
} from "../../../modules/validateInvoiceForm";
import { clearFieldError, handleErrors } from "../../../utils/handleErrors";
import useLineItems from "../../../modules/lineItem";
import { useUserStore } from "../../../state";
import LogoComponent from "../create/SalesCreate/LogoComponent";
import LineTable from "../create/SalesCreate/LineTable";
import InvoiceCalculation from "../create/SalesCreate/InvoiceCalculation";
import BankDetails from "../create/SalesCreate/BankDetails";
import Notes from "../create/SalesCreate/Notes";
import CustomerCreateInfo from "../create/SalesCreate/CustomerInfo";
import { updateProfile } from "../../../backend-services";
import CreateInvoice from "../create/SalesCreate/CreateInvoice";
import InvoiceCurrency from "../create/SalesCreate/InvoiceCurrency";
import { CreateModal } from "../../common/CreateModal";
import FreeformTable from "../create/SalesCreate/FreeformTable";
import SuccessBanner from "../../common/Banner/SuccessBanner";
import { validateExpenseItemDetails } from "../../../modules/expense/expense";
import { checkSubscription } from "../../../utils/subscription";
import { servicesType } from "../../../constants/Subscription";
import {
  validateQuoteBasicInfo,
  validateQuoteItemDetails,
  validateQuoteSummary,
} from "../../../modules/validateQuoteForm";
import QuoteDate from "../create/SalesCreate/QuoteDate";
import { useTranslation } from "react-i18next";
import CompanyUpdate from "../create/SalesCreate/companyProfile";
import { useCompanyStore } from "../../../state/useCompanyStore";
import { useGetPaymentCurrencyList } from "../../../utils";
import LineItemsFreeform from "../../Invoice/sections/WithInventory.tsx/lineItemWithout";
import ServiceFreeformLineItem from "../../Invoice/Service/ServiceFreeformItem";
import MobileFreeForm from "./MobileFreeForm";
import CustomerSection from "../../Invoice/sections/CustomerSection";
import CreateQuote from "../create/SalesCreate/CreateQuote";
import MobileServiceFreeform from "./MobileServiceFreeForm";
import { showToast } from "../../../utils/toastUtils";

interface QuoteFormProps {
  customerId?: string;
  quoteData: ICreateSalesData;
  setQuoteData: React.Dispatch<React.SetStateAction<ICreateSalesData>>;
  headingText: string;
  updatedNewItem?: ItemInput;
  onCreateQuote?: (event: any) => Promise<void>;
  showSidebar?: boolean;
}
const QuoteFreeform: React.FC<QuoteFormProps> = (props) => {
  const { t } = useTranslation("quotes");
  const userStore = useCompanyStore();
  const { userCurrencyCode, currencyList } = useGetPaymentCurrencyList(
    userStore.company,
  );
  const { quoteNo } = useParams<{ quoteNo: string }>();
  const [isEditing, setIsEditing] = useState<boolean>(!!quoteNo);
  const isRetail = userStore.isRetailBusiness();
  const [formData, setFormData] = React.useState<ICreateSalesData>({
    additionalInformation: "",
    customerId: "",
    description: "",
    currency: userCurrencyCode.currency,
    discount: 0,
    inventories: [
      {
        id: "",
        name: "",
        amount: 0,
        quantity: 0,
        rate: 0,
        variations: [
          {
            type: "",
            name: "",
            id: "",
            price: 0,
            quantity: 0,
          },
        ],
      },
    ],
    services: [{ id: "", name: "", duration: "", price: 0 }],
    expiryDate: "",
    invoiceNumber: "",
    issueDate: new Date().toISOString().substring(0, 10),
    isReoccurring: false,
    isReoccurringEndDate: "",
    isReoccurringStartDate: "",
    reoccurringGroupName: "",
    status: "active",
    tax: 0,
    isPaymentCompleted: false,
    shouldSendEmail: true,
    exchangeRate: 1,
    paymentMethod: "",
    invoiceType: "freeform",
    logoBase64: "",
  });

  React.useEffect(() => {
    if (props.quoteData) {
      setFormData((prevState: any) => ({
        ...prevState,
        customerId: props.quoteData.customerId,
        customer: props.quoteData.customerId ? customer : null,
        description: props.quoteData.description,
        discount: props.quoteData.discount,
        inventories: props.quoteData.inventories,
        services: props.quoteData.services,
        expiryDate: props.quoteData.expiryDate,
        issueDate: props.quoteData.issueDate,
        shippingCharge: props.quoteData.shippingCharge,
        status: props.quoteData.status,
        tax: props.quoteData.tax,
        shouldSendEmail: props.quoteData.shouldSendEmail,
        currency: props.quoteData.currency,
        exchangeRate: props.quoteData.exchangeRate,
        invoiceType: props.quoteData.invoiceType,
      }));
    }
  }, [props.quoteData]);

  useEffect(() => {
    if (isEditing && props.quoteData) {
      const {
        customer,
        inventories = [],
        services = [],
        customerId,
        currency,
        status,
        exchangeRate,
      } = props.quoteData;

      setFormData((prev) => ({
        ...prev,
        customerId: customerId || "",
        currency: currency || userCurrencyCode.currency,
        status: status || "active",
        exchangeRate: exchangeRate || 1,
        inventories: inventories || [],
        services: services || [],
      }));

      setItems(isRetail ? inventories : services);

      if (customer) {
        setCustomer(customer);
      }
    }
  }, [isEditing, props.quoteData]);

  const initialFormData: ICreateSalesData = {
    additionalInformation: "",
    customerId: "",
    description: "",
    currency: userCurrencyCode.currency,
    discount: 0,
    inventories: [
      {
        id: "",
        name: "",
        amount: 0,
        quantity: 0,
        rate: 0,
        variations: [
          {
            type: "",
            name: "",
            id: "",
            price: 0,
            quantity: 0,
          },
        ],
      },
    ],
    services: [{ id: "", name: "", duration: "", price: 0 }],
    invoiceDueAt: "",
    invoiceNumber: "",
    issueDate: new Date().toISOString().substring(0, 10),
    shippingCharge: 0,
    shippingDesc: "",
    hasShipping: true,
    isReoccurring: false,
    isReoccurringEndDate: "",
    isReoccurringStartDate: "",
    reoccurringGroupName: "",
    status: "pending",
    tax: 0,
    isPaymentCompleted: false,
    shouldSendEmail: true,
    exchangeRate: 1,
    paymentMethod: "",
    invoiceType: "freeform",
    logoBase64: "",
    lineItems: "",
  };

  const resetForm = () => {
    setFormData({
      ...(initialFormData as ICreateSalesData),
      invoiceDueAt: "",
      additionalInformation: "",
    });
    setValidationResults({});
    setCustomer(null);

    // Reset line items (inventories and services)
    setItems([{ id: "", name: "", rate: 0, quantity: 0 }]);
    setShowSidebar(false);
  };

  const [createdQuote, setCreatedQuote] = useState(null);
  const [validationResults, setValidationResults] =
    React.useState<InvoiceErrors>({});
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = React.useState<ICustomer | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDraftLoading, setIsisDraftLoading] = React.useState<boolean>(false);
  const [subTotal, setSubtotal] = React.useState<number>(0);
  const [total, setTotal] = React.useState<number>(0);
  const [calcTax, setCalcTax] = React.useState(0);
  const [calcShipping, setCalcShipping] = React.useState(0);
  const [calcDiscount, setCalcDiscount] = React.useState(0);
  const [showSidebar, setShowSidebar] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [isReoccurringChecked, setIsReoccurringChecked] = React.useState(false);
  const [showCreateSidebar, setShowCreateSidebar] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [createdQuoteId, setCreatedQuoteId] = React.useState("");
  const [createdQuoteLink, setCreatedQuoteLink] = React.useState("");
  const [createdEmail, setCreatedEmail] = React.useState("");
  const [showRateInput, setShowRateInput] = useState(false);
  const [markAsPaid, setMarkAsPaid] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(false);
  const [shouldSendWhatsApp, setShouldSendWhatsApp] = React.useState(false);
  const [shouldSendSMS, setShouldSendSMS] = React.useState(false);
  const [selectedCurrencyLabel, setSelectedCurrencyLabel] = React.useState(
    userCurrencyCode.currency,
  );
  const [selectedVariation, setSelectedVariation] = useState<
    string | undefined
  >(undefined);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const [error, setError] = React.useState<string>("");
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 508); // Track the screen size state
  const [bannerLinkText, setBannerLinkText] = useState("");
  const [selectedInventory, setSelectedInventory] = useState<IInventory | null>(
    null,
  );

  const handleViewSelection = (inventory: IInventory) => {
    setSelectedInventory(inventory);
    setShowSidebar(true);
  };

  const [draftValidationResults, setDraftValidationResults] =
    React.useState<InvoiceValidationErrors>({
      customerId: undefined,
      invoiceNumber: undefined,
      invoiceDueAt: "",
      description: undefined,
      status: undefined,
      issueDate: undefined,
      additionalInformation: undefined,
      shippingCharge: undefined,
      discount: undefined,
      tax: undefined,
      isReoccurring: undefined,
      isReoccurringStartDate: undefined,
      isReoccurringEndDate: undefined,
      reoccurringGroupName: undefined,
      task: undefined,
      inventories: undefined,
      services: undefined,
      currency: undefined,
      exchangeRate: undefined,
      paymentMethod: undefined,
      invoiceType: undefined,
    });

  // Set initial items based on screen size
  const initialItems = isMobile
    ? []
    : [{ id: "", name: "", rate: 0, quantity: 0 }];

  const { items, addItem, setItems, removeItem, updateCell, updateRow } =
    useLineItems(initialItems);

  const deepCompare = (arr1: any[], arr2: any[]) => {
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item, index) => {
      return JSON.stringify(item) === JSON.stringify(arr2[index]);
    });
  };

  const prevItemsRef = useRef<ItemInput[]>([]);

  // Watch for screen resize events
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setItems([]);
    } else if (
      items.length === 0 ||
      !items.some((item) => item.name || item.rate || item.quantity)
    ) {
      // Set default items if empty on desktop view
      setItems([{ id: "", name: "", rate: undefined, quantity: 0 }]);
    }
  }, [isMobile]); // Trigger on mobile size changes

  React.useEffect(() => {
    // Clear field errors
    clearFieldError("inventories", validationResults, setValidationResults);
    clearFieldError("lineItems", validationResults, setValidationResults);
    clearFieldError("services", validationResults, setValidationResults);

    const lineItemsTotal = items.reduce((total, item) => {
      const rate = Number(item?.rate) || 0;
      const quantity = Number(item?.quantity) || 0;
      const price = Number(item?.price) || 0;
      const duration = Number(item?.duration) || 0;
      return total + (rate * quantity || price * duration);
    }, 0);

    const lineItems = items.map((item) => ({
      ...item,
      amount:
        (Number(item?.rate) || 0) * (Number(item?.quantity) || 0) ||
        (Number(item?.price) || 0) * (Number(item?.duration) || 0),
    }));

    const inventories = items.map((item) => ({
      id: item.id,
      name: item.name,
      amount: 0,
      quantity: Number(item.quantity) || 0,
      rate: Number(item.rate) || 0,
      variations: Array.isArray(item.variation) ? item.variation : [],
    }));

    setFormData((prevState) => {
      if (
        JSON.stringify(prevState.lineItems) === JSON.stringify(lineItems) &&
        JSON.stringify(prevState.inventories) === JSON.stringify(inventories) &&
        JSON.stringify(prevState.services) === JSON.stringify(items)
      ) {
        return prevState;
      }

      return {
        ...prevState,
        lineItems,
        inventories,
        services: items,
      };
    });

    setTotal(lineItemsTotal);
  }, [items]);

  const handleInputChange = (event: { target: { name: any; value: any } }) => {
    const { name, value } = event.target;
    setFormData((prevData) => {
      return { ...prevData, [name]: value };
    });
    clearFieldError(name, validationResults, setValidationResults);
  };

  useEffect(() => {
    let subTotal = 0;

    // Calculate subtotal based on inventories
    if (formData.inventories && formData.inventories.length > 0) {
      subTotal += formData.inventories.reduce(
        (total, item) => total + (item?.rate || 0) * (item?.quantity || 0),
        0,
      );
    }

    // Calculate subtotal based on services
    if (formData.services && formData.services.length > 0) {
      subTotal += formData.services.reduce(
        (total: number, service: { price: any; duration: any }) =>
          total + (service?.price || 0) * (service?.duration || 0),
        0,
      );
    }

    // Calculate tax and discount
    const discountAmount =
      subTotal * (formData?.discount ? formData.discount / 100 : 0);
    const taxAmount = subTotal * (formData?.tax ? formData.tax / 100 : 0);
    const shippingAmount = Number(formData?.shippingCharge) || 0
    const totalAmount = subTotal - discountAmount + taxAmount + shippingAmount;
   

    // Update all states at once to reduce re-renders
    setSubtotal(subTotal);
    setCalcTax(taxAmount);
    setCalcDiscount(discountAmount);
    setCalcShipping(shippingAmount)
    setTotal(totalAmount);

    // Update formData.total without triggering another calculation
    setFormData((prevData) => ({
      ...prevData,
      total: totalAmount.toFixed(2).toString(),
    }));
  }, [
    formData.inventories,
    formData.services,
    formData.discount,
    formData.tax,
    formData.shippingCharge
  ]);

  useEffect(() => {
    handleDropDownChange("currency", selectedCurrencyLabel);
  }, []);

  const handleDropDownChange = (name: string, value: string) => {
    clearFieldError(name, validationResults, setValidationResults);
    const currencyValue =
      value === userCurrencyCode.currency ? userCurrencyCode.currency : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: currencyValue || userCurrencyCode.currency,
    }));
    setSelectedCurrencyLabel(value);
    setShowRateInput(currencyValue !== userCurrencyCode.currency);
  };

  const handlePaymentDropDownChange = (name: string, value: string) => {
    clearFieldError(name, validationResults, setValidationResults);
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const location = useLocation();
  const { customerId } = useParams();
  React.useEffect(() => {
    if (customerId) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        name: decodeURIComponent(customerId || ""),
      }));
    }
  }, [customerId]);

  const handleSetCustomer = (selectedCustomer: ICustomer | null) => {
    if (selectedCustomer) {
      setCustomer(selectedCustomer);
      if (selectedCustomer.id) {
        setFormData((prevFormData: any) => ({
          ...prevFormData,
          customerId: selectedCustomer.id,
          name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        }));
      }
    } else {
      setCustomer(null);
      setFormData((prevFormData: any) => ({
        ...prevFormData,
        customerId: "",
        name: "",
      }));
    }
  };

  const setLogoUrl = (url: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      logoBase64: url,
    }));
  };

  const handleEditProfileLogo = async () => {
    setIsLoading(true);
    try {
      const updatedLogo = { brandLogo: formData.logoBase64 };
      await updateProfile(updatedLogo);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setErrorMessage("Unable to update profile logo.");
    }
  };

  useEffect(() => {
    if (
      typeof formData.exchangeRate !== "number" &&
      isNaN(Number(formData.exchangeRate))
    ) {
      // Set the exchange rate to 1
      setFormData((prevData) => ({
        ...prevData,
        exchangeRate: 1,
      }));
    }
  }, [formData.exchangeRate]);

  useEffect(() => {
    // Update formData when items change
    setFormData((prevState) => ({
      ...prevState,
      lineItems: items.map((item) => ({
        ...item,
        amount: (Number(item?.duration) || 0) * (Number(item?.price) || 0),
      })),
    }));
  }, [items]);

  const handleViewInvoice = () => {
    if (createdQuoteLink) {
      const viewInvoiceRoute = `/quotes/${createdQuoteLink}/view`;
      navigate(viewInvoiceRoute);
    }
  };

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
  };

  const requiredFields = [
    "customerId",
    "issueDate",
    "expiryDate",
    "lineItems", // or "services" depending on the business type
  ];

  const processCreateQuote = async (
    sendEmail: boolean,
    markedAsPaid: boolean,
    shouldSendWhatsApp: boolean,
    shouldSendSMS: boolean,
    quoteNo?: string,
  ) => {
    try {
      setIsLoading(true);
      setShowCreateSidebar(false);

      const {
        expiryDate,
        issueDate,
        inventories,
        services,
        discount,
        tax,
        customerId,
        lineItems,
      } = formData;
      const updatedServices = services.map((service: { duration: any }) => ({
        ...service,
        duration: String(service.duration),
      }));
      const user = userStore.company;
      const isRetail = userStore.isRetailBusiness();
      const validExpiry = expiryDate ? new Date(expiryDate) : null;
      const validIssueDate = issueDate ? new Date(issueDate) : null;

      if (!validExpiry || isNaN(validExpiry.getTime())) {
        throw new Error("Invalid expiry date");
      }

      if (!validIssueDate || isNaN(validIssueDate.getTime())) {
        throw new Error("Invalid issue date");
      }
      const quoteData: ICreateSalesData = {
        customerId: customerId,
        shouldSendEmail: sendEmail,
        isPaymentCompleted: markedAsPaid,
        shouldSendWhatsApp: shouldSendWhatsApp,
        shouldSendSMS: shouldSendSMS,
        expiryDate: validExpiry.toISOString(),
        issueDate: validIssueDate.toISOString(),
        lineItems: lineItems.map(
          (i: {
            id: string;
            name: string;
            rate: number;
            quantity: number;
          }) => ({
            id: i.id,
            name: i.name,
            amount: 0,
            quantity: Number(i.quantity),
            rate: Number(i.rate),
          }),
        ),
        inventories: isRetail
          ? inventories.length
            ? inventories.map(
                (i: {
                  id: string;
                  name: string;
                  rate: number;
                  quantity: number;
                  variations?: any;
                }) => ({
                  id: i.id,
                  name: i.name,
                  amount: 0,
                  quantity: Number(i.quantity),
                  rate: Number(i.rate),
                  variations: i.variations ? i.variations : undefined,
                }),
              )
            : []
          : [],
        services: !isRetail
          ? updatedServices.length
            ? updatedServices.map((service: { duration: any; price: any }) => ({
                ...service,
                duration: String(service.duration),
                price: Number(service.price),
                total: Number(service.price) * Number(service.duration),
              }))
            : []
          : [],
        discount: Number(discount ? discount : 0),
        tax: Number(tax ? tax : 0),
        hasShipping: formData.hasShipping || !!formData.shippingCharge,
        shippingCharge: Number(formData.shippingCharge || 0),
        shippingDesc: formData.shippingDesc || "",
        status: formData.status || "pending",
        description: formData.description || "",
        additionalInformation: formData.additionalInformation || "",
        currency: formData.currency || "",
        exchangeRate: formData.exchangeRate,
        paymentMethod: formData.paymentMethod || "",
        invoiceType: "freeform",
      };
      if (customer) {
        if (quoteNo) {
          const taxValue = formData.tax ? Number(formData.tax) / 100 : 0;
          const formattedTax = taxValue.toFixed(2);
          const updatedQuoteData = {
            expiryDate: validExpiry.toISOString(),
            issueDate: validIssueDate.toISOString(),
            discount: Number(formData.discount || 0),
            tax: Number(formattedTax),
            hasShipping: formData.hasShipping || !!formData.shippingCharge,
            shippingCharge: Number(formData.shippingCharge || 0),
            shippingDesc: formData.shippingDesc || "",
            ...(isRetail
              ? {
                  inventories: inventories.length
                    ? inventories.map(
                        (i: {
                          id: string;
                          name: string;
                          rate: number;
                          quantity: number;
                          variations?: any;
                        }) => ({
                          id: i.id,
                          name: i.name,
                          amount: 0,
                          quantity: Number(i.quantity),
                          rate: Number(i.rate),
                          variations:
                            i.variations.length > 0 ? i.variations : undefined,
                        }),
                      )
                    : [],
                }
              : {
                  services: services.length
                    ? services.map(
                        (s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                          id: s.id,
                          name: s.name,
                          duration: s.duration,
                          price: Number(s.price),
                        }),
                      )
                    : [],
                }),
          };

          const createdUpdatedQuote = await updateQuote({
            id: quoteNo,
            data: updatedQuoteData,
          });
          const createdUpdatedQuoteId = createdUpdatedQuote?.id;
          const createdEmail = customer.email || "";

          setCreatedQuote(createdUpdatedQuoteId);
          setCreatedEmail(createdEmail);
          setBannerMessage("Quote Updated.");
          setBannerLinkText("View Quote");
          setIsBannerVisible(true);
        } else {
          const createdQuote = await createQuoteFreeformData(
            quoteData,
            customer,
            isRetail,
          );
          const createdQuoteId = createdQuote?.id;
          const createdEmail = customer.email || "";
          setCreatedQuoteLink(createdQuoteId);
          setCreatedEmail(createdEmail);

          setBannerMessage("New Quote Created.");
          setBannerLinkText("View Quote");
          setIsBannerVisible(true);
        }
      } else {
      }
    } catch (error: any) {
      handleErrors(error, setErrorMessage);
      setErrorMessage("An error occurred while processing the quote.");
      showToast("An error occurred while processing the quote.", "error");
      if (error.response) {
        const errorMessage = error.response.data;
        setError(errorMessage);
      }
      setIsLoading(false);
    }
  };

  const handleCreateQuote = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    try {
      const basicInfoValid = validateQuoteBasicInfo(
        formData,
        setValidationResults,
      );
      const itemDetailsValid = validateQuoteItemDetails(
        formData,
        setValidationResults,
        userStore.isRetailBusiness(),
      );
      const summaryValid = validateQuoteSummary(formData, setValidationResults);
      if (basicInfoValid && summaryValid) {
        setShowSidebar(true);
      } else {
        setErrorMessage("Please fill out fields correctly.");
        showToast("Please fill out fields correctly.", "error");
        const errorElements = [
          { id: "customer-info", hasError: !basicInfoValid },
          { id: "quote-date", hasError: !basicInfoValid },
          { id: "quote-currency", hasError: !basicInfoValid },
          {
            id: "line-table",
            hasError: formData.invoiceType === "freeform" && !itemDetailsValid,
          },
        ];

        const firstErrorElement = errorElements.find((el) => el.hasError);

        if (firstErrorElement) {
          const element = document.getElementById(firstErrorElement.id);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }
    } catch (error) {
      showToast("Failed to create quote. Please try again later.", "error");
    }
  };

  const onCreateQuote = async () => {
    await processCreateQuote(
      sendEmail,
      markAsPaid,
      shouldSendWhatsApp,
      shouldSendSMS,
      quoteNo,
    );

    resetForm();
    setIsBannerVisible(true);
    setShowSidebar(false);
  };

  function handleSendEmailChange(checked: boolean) {
    setSendEmail(checked);
  }
  function handleMarkAsPaidChange(event: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) {
    setMarkAsPaid(event.target.checked);
  }
  function handleshouldSendWhatsAppChange(checked: boolean) {
    setShouldSendWhatsApp(checked);
  }
  function handleshouldSendSMSChange(checked: boolean) {
    setShouldSendSMS(checked);
  }
  const handleIsReoccurringChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    setFormData((prevData) => ({
      ...prevData,
      isReoccurring: target.checked,
    }));
    setIsReoccurringChecked(target.checked);
  };
  useEffect(() => {
    if (isEditing && props.quoteData) {
      const { inventories, services } = props.quoteData;
      setItems(isRetail ? inventories : services);
    }
  }, [isEditing, props.quoteData, isRetail]);
  function hasValidInventoryWithName(formData: ICreateSalesData) {
    // Check if formData exists and inventories is an array
    if (!formData || !Array.isArray(formData.lineItems)) {
      return false;
    }

    // Check if any inventory has a valid name (non-empty string)
    return formData.lineItems.some(
      (inventory) =>
        inventory &&
        typeof inventory.name === "string" &&
        inventory.name.trim() !== "",
    );
  }
  return (
    <>
      {isBannerVisible && (
        <SuccessBanner
          message={bannerMessage}
          linkText={bannerLinkText}
          onLinkClick={handleViewInvoice}
          onClose={handleCloseBanner}
        />
      )}
      <div className="flex items-left gap-[8.5px] ml-[2em] ">
        <button onClick={() => navigate(-1)}>
          <svg
            className="mt-[-2px] ml-[-2em] "
            width="10"
            height="16"
            viewBox="0 0 10 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8.5 1L1.5 8L8.5 15" stroke="#33363F" strokeWidth="2" />
          </svg>
        </button>
        <h1 className="hidden lg:block md:block flex items-center xl:text-[20px] text-[16px] ml-[-1em] mt-[-0.4em] xl:mt-[-0.4em] font-bold">
          {isEditing ? " Edit Quote" : " Create Quote"}
        </h1>
        <h1 className="lg:hidden md:hidden flex items-center xl:text-[20px] text-[16px] ml-[-1em] mt-[-0.4em] xl:mt-[-0.4em] font-bold">
          {isEditing ? " Edit Quote" : " Create Quote"}
        </h1>
      </div>

      <div
        className="mx-auto h-full bg-white lg:ml-0 border-2 border-gray-100 mt-3 "
        id="invoice-parent"
      >
        {errorMessage && (
          <p className="text-[red] lg:pl-[3em] pl-3 md:pl-[3em] mt-3">
            {errorMessage}
          </p>
        )}
        <div className="md:flex md:justify-between lg:pt-[4em] pt-[2em] ">
          <div className="xl:pl-[3em] px-3 md:pl-[2em]">
            <h1 className="font-semibold">Your Logo</h1>
            <LogoComponent
              logoUrl={formData.logoBase64 || ""}
              setLogoUrl={setLogoUrl}
              error={undefined}
              onUploadSuccess={handleEditProfileLogo}
            />
          </div>
          {/* <h1 className="xl:text-[24px] biglg:text-[24px] text-[14px] font-medium lg:px-[3em] pr-3 md:pr-[3em]">
            QUOTE
          </h1> */}
        </div>
        <div className="hidden lg:block md:block">
          <div className="flex justify-between ">
            <div className="flex flex-col mt-[1em]">
              <div className="pl-3 md:pl-[2em] ">
                <CompanyUpdate
                  show={true}
                  toolTipText={t("businessNameTooltip")}
                  toolTipText2={t("businessAddressTooltip")}
                  toolTipText3={t("phoneNumberTooltip")}
                  toolTipText4={t("RCNumberTooltip")}
                />
              </div>
              <div
                id="customer-info"
                className="lg:pl-[2em] pl-3 md:pl-[2em] mt-[-1em]"
              >
                <CustomerCreateInfo
                  handleSetCustomer={handleSetCustomer}
                  formData={formData}
                  customer={customer}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  toolTipText={t("billTooltip")}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div id="quote-date" className="pr-[1em] md:pr-[4em] mt-[-10em]">
                <QuoteDate
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  toolTipText={t("quote.IssuedTooltip")}
                  toolTipText2={t("quote.dueTooltip")}
                  handleInputChange={handleInputChange}
                />
              </div>
              <div id="invoice-currency" className="lg:mt-[1em]">
                <InvoiceCurrency
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  showRateInput={showRateInput}
                  toolTipText={t("currencyTooltip")}
                  currencyList={currencyList}
                  localCurrency={userCurrencyCode.currency}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="lg:hidden md:hidden">
          <hr className="my-[1em]" />
          <div className="flex  flex-col-reverse justify-between ">
            <div className="flex flex-col ">
              <hr className="my-[1em]" />
              <div
                id="invoice-currency"
                className="lg:mt-[3em] mt-[-1em] px-[1em]"
              >
                <InvoiceCurrency
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  showRateInput={showRateInput}
                  currencyList={currencyList}
                  localCurrency={userCurrencyCode.currency}
                />
              </div>
            </div>
            <div className="">
              <div id="quote-date" className="px-[1em] md:pr-[3em]">
                <QuoteDate
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  toolTipText={t("quote.IssuedTooltip")}
                  toolTipText2={t("quote.dueTooltip")}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <hr className="my-[1em]" />

          <div className="px-[1em] mt-[2em]">
            <CompanyUpdate show={true} />
          </div>

          <hr className="my-[1em]" />
          <div
            id="customer-info"
            className="lg:pl-[3em] px-[1em] md:pl-[3em] mt-[-1em]"
          >
            <CustomerSection
              customer={customer}
              handleSetCustomer={handleSetCustomer}
              validationResults={validationResults}
              formData={formData}
              // setFormData={setFormData}
              setValidationResults={setValidationResults}
            />
          </div>
        </div>

        <hr className="lg:hidden md:hidden mt-[2em]" />
        {/* <p className="lg:hidden md:hidden text-[9px] font-semibold flex justify-center text-center mt-3">
          Item Details
        </p> */}

        <div className="px-[1em] md:hidden lg:hidden">
          {isRetail ? (
            <MobileFreeForm
              formData={formData}
              setFormData={setFormData}
              selectedCurrencyLabel={selectedCurrencyLabel}
              validationResults={validationResults}
              setValidationResults={setValidationResults}
              items={items}
              setItems={setItems}
              addItem={addItem}
              removeItem={removeItem}
              updateCell={updateCell}
              updateRow={updateRow}
            />
          ) : (
            <div>
              <MobileServiceFreeform
                formData={formData}
                setFormData={setFormData}
                selectedCurrencyLabel={selectedCurrencyLabel}
                validationResults={validationResults}
                setValidationResults={setValidationResults}
                items={items}
                setItems={setItems}
                addItem={addItem}
                removeItem={removeItem}
                updateCell={updateCell}
                updateRow={updateRow}
              />
            </div>
          )}
        </div>
        <hr className="md:hidden lg:hidden my-4" />
        <div
          id="line-table"
          className="lg:mt-[6em] mt-[-1em]  md:mt-[5em] px-[2em]  hidden md:block"
        >
          <FreeformTable
            items={items}
            addItem={addItem}
            setItems={setItems}
            removeItem={removeItem}
            updateCell={updateCell}
            updateRow={updateRow}
            formData={formData}
            setFormData={setFormData}
            selectedCurrencyLabel={selectedCurrencyLabel}
            validationResults={validationResults}
            setValidationResults={setValidationResults}
          />
        </div>
        <div className="hidden lg:block md:block">
          <div className="pr-[3em] flex justify-between items-end">
            <div className="pl-[3em] flex flex-col justify-between h-full">
              <BankDetails />
            </div>
            <div className="flex flex-col justify-between h-full ">
              <InvoiceCalculation
                 subTotal={subTotal}
                 formData={formData}
                 calcDiscount={calcDiscount}
                 calcTax={calcTax}
                 calcShipping={calcShipping}
                 total={total}
                 handleInputChange={handleInputChange}
                 isRetail={isRetail} 
              />
            </div>
          </div>
        </div>
        <div className="lg:justify-end md:hidden lg:hidden">
          <div className="lg:pr-[3em] px-[1em]">
            <InvoiceCalculation
              subTotal={subTotal}
              formData={formData}
              calcDiscount={calcDiscount}
              calcTax={calcTax}
              total={total}
              handleInputChange={handleInputChange}
              isRetail={isRetail}
            />
          </div>
        </div>
        <hr className="border mt-[3em]" />
        <div className="hidden lg:block md:block">
          <div className=" flex justify-start md:gap-[3em]">
            <div className="pl-[3em]">
              <Notes
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        <div className="lg:hidden md:hidden flex flex-col">
          <div className="px-[1em]">
            <Notes formData={formData} handleInputChange={handleInputChange} />
          </div>
          <hr className="my-[1.3em]" />
          <div className="px-[1em]">
            <BankDetails />
          </div>
        </div>
        <hr className="border mt-[3em]" />
        <div className="flex justify-end ">
          <div className="hidden md:block mt-5 pb-6 pr-[1em]">
            <Button
              text={isEditing ? "Save" : "Create Quote"}
              size="medium"
              type="submit"
              onClick={isEditing ? onCreateQuote : handleCreateQuote}
              variant="secondary"
              disabled={!hasValidInventoryWithName(formData)}
            />
          </div>
        </div>
        <div className="px-[1em] md:hidden lg:hidden">
          <div className="md:hidden mt-5 mb-4">
            <Button
              text={isEditing ? "Save" : "Create Quote"}
              size="full"
              type="submit"
              onClick={isEditing ? onCreateQuote : handleCreateQuote}
              variant="secondary"
              disabled={!hasValidInventoryWithName(formData)}
            />
          </div>
        </div>
        {showSidebar && (
          <CreateModal>
            <CreateQuote
              formData={formData}
              handleInputChange={handleInputChange}
              handlePaymentDropDownChange={handlePaymentDropDownChange}
              handleIsReoccurringChange={handleIsReoccurringChange}
              validationResults={validationResults}
              invoiceNo={invoiceNo}
              sendEmail={sendEmail}
              markAsPaid={markAsPaid}
              shouldSendWhatsApp={shouldSendWhatsApp}
              shouldSendSMS={shouldSendSMS}
              handleMarkAsPaidChange={handleMarkAsPaidChange}
              handleSendEmailChange={handleSendEmailChange}
              handleshouldSendWhatsAppChange={handleshouldSendWhatsAppChange}
              handleshouldSendSMSChange={handleshouldSendSMSChange}
              errorMessage={errorMessage}
              error={error}
              customer={customer}
              showMarkAsPaid={true}
              onCreateQuote={onCreateQuote}
              isLoading={isLoading}
              onClose={() => setShowSidebar(false)}
              setValidationResults={setValidationResults}
            />
          </CreateModal>
        )}
      </div>
    </>
  );
};

export default QuoteFreeform;
